import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  Menu,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
} from '@mui/material';
import {
  Save as SaveIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { BglPrimaryButton } from 'components/button/BglButtons';
import { useGlobalConfig } from 'context/GlobalContext';
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { ArrowLeft, Key, Plus, Info, ChevronDown, X, Download, AlertTriangle, Layers } from 'lucide-react';
import { useSelector } from 'react-redux';
import { selectPilotList, selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { ClusterApis } from 'apis/clusterAPI';
import { PilotApis } from 'apis/pilotAPI';
import { DCXPromotionApis } from 'apis/DCXPromotionAPI';
import type { ProjectEnvironment } from 'types/apis/pilotInterfaces';
import { featureIdFuel, resolveFeatureDisplayName, withFuelLabel } from 'utils/feature-id';
import { projectEnvironmentToAppEnv, getImportSourceAppEnvs, formatAppEnvLabel } from 'features/contentManagement/mediaContent/mediaEnvMapping';
import { useToast } from 'context/ToastContext';
import { PermissionGate } from 'components/access/PermissionGate';
import { UI_PAGES } from 'framework/access/UiRegistry';
import { 
  AccessButton, 
  AccessStatusDropdown, 
  getStatusIcon, 
  AccessStatus 
} from 'components/common/AccessStatusButton';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease-in-out',
}));

// API response item interface
interface FeatureMetricItem {
  featureId: string;
  featureName: string;
  clusterReference: string;
  resolution: AccessStatus;
  [key: string]: any;
}

// Transformed feature structure for display
interface Feature {
  id: string;
  name: string;
  clusterResolutions: { [clusterReference: string]: AccessStatus };
}

/** Row label — the API's feature name, corrected for the featureIds it gets wrong, plus the
 *  fuel, since `DASHBOARD.ELECTRIC` and `DASHBOARD.GAS` both name themselves "Dashboard".
 *  Shared with the DCX compare table; the raw featureId stays in the tooltip. */
const featureLabel = (feature: Feature): string =>
  withFuelLabel(resolveFeatureDisplayName(feature.name, feature.id), featureIdFuel(feature.id));

/** Env label for the import dialog — "Non-Prod (Sandbox)" style: name plus its stack. */
const envLabel = (env: ProjectEnvironment): string => {
  const name = String(env.environment_name ?? env.name ?? env.id);
  return env.stack_name ? `${name} (${env.stack_name})` : name;
};

export const FeatureAccessPage: React.FC = () => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const toast = useToast();
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const pilotList = useSelector(selectPilotList) as any[];
  const selectedPilot = React.useMemo(
    () => pilotList.find((p) => p?.projectId === selectedPilotId),
    [pilotList, selectedPilotId]
  );
  
  // State
  const [features, setFeatures] = useState<Feature[]>([]);
  /** Snapshot from the last fetch/stage — Stage Changes diffs against this, not against
   *  every visible cell, since the stage endpoint is update-only and per-cell. */
  const [originalFeatures, setOriginalFeatures] = useState<Feature[]>([]);
  /** `${featureId}::${clusterReference}` keys currently in the target's staged matrix — drives
   *  the purple "staged" cell color. Only populated for nonprod/prod (see isStageOnlyEnv). */
  const [stagedKeys, setStagedKeys] = useState<Set<string>>(new Set());
  const [clusterColumns, setClusterColumns] = useState<string[]>([]);
  const [availableClusters, setAvailableClusters] = useState<{ clusterReference: string; clusterName: string; clusterId: string }[]>([]);
  const [filter, setFilter] = useState('all');
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dropdownAnchor, setDropdownAnchor] = useState<{ [key: string]: HTMLElement | null }>({});
  const [addClusterAnchor, setAddClusterAnchor] = useState<null | HTMLElement>(null);
  const addClusterOpen = Boolean(addClusterAnchor);

  // Import Access Matrix
  const [environments, setEnvironments] = useState<ProjectEnvironment[]>([]);
  const [importOpen, setImportOpen] = useState(false);
  const [importSourceEnvId, setImportSourceEnvId] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  /** Bumped after an applied import so the matrix below reloads. */
  const [reloadKey, setReloadKey] = useState(0);

  /** The import always targets the environment this page is showing. */
  const currentEnvId = selectedPilot?.environment_id ? String(selectedPilot.environment_id) : '';
  const currentEnv = environments.find((e) => String(e.id) === currentEnvId);
  /** nonprod/prod targets can't be written to directly — Save Changes becomes Stage Changes,
   *  which edits the already-staged matrix as one batch (see DCXPromotionApis.stageSheetRows). */
  const currentAppEnv = currentEnv ? projectEnvironmentToAppEnv(currentEnv) : null;
  const isStageOnlyEnv = currentAppEnv === 'nonprod' || currentAppEnv === 'prod';

  /**
   * Import sources, per the promotion matrix (PROMOTION_TARGETS, read backwards). Only the
   * staged environments are constrained: nonprod and prod may be fed from UAT alone. Anywhere
   * else keeps every associated environment as a candidate.
   */
  const importSourceOptions = environments.filter((env) => {
    if (String(env.id) === currentEnvId) return false;
    if (!isStageOnlyEnv || !currentAppEnv) return true;
    const sourceAppEnv = projectEnvironmentToAppEnv(env);
    return !!sourceAppEnv && getImportSourceAppEnvs(currentAppEnv).includes(sourceAppEnv);
  });

  /** Sources the matrix allows but the pilot has no environment for. Listed disabled rather
   *  than omitted, so a short (or empty) dropdown explains itself. */
  const unmappedSourceAppEnvs = (currentAppEnv ? getImportSourceAppEnvs(currentAppEnv) : []).filter(
    (appEnv) => !environments.some((env) => projectEnvironmentToAppEnv(env) === appEnv)
  );

  // Track page load on mount
  useEffect(() => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.FEATURE_ACCESS_LOADED)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch feature access data on mount
  useEffect(() => {
    const fetchFeatureAccess = async () => {
      if (!selectedPilotId) {
        return;
      }

      try {
        setLoading(true);
        const response = await ClusterApis.getFeatureMetric(rc, selectedPilotId.toString(), 'LEVEL_1');
        
        if (response.success && response.data) {
          // Get the raw data array
          const responseData = response.data as any;
          const rawData: FeatureMetricItem[] = responseData.features || responseData.data || responseData;
          
          if (Array.isArray(rawData)) {
            // Extract unique clusterReference values for columns
            const uniqueClusters = [...new Set(rawData.map((item: FeatureMetricItem) => 
              item.clusterReference || item.cluster_reference || ''
            ).filter(Boolean))];
            setClusterColumns(uniqueClusters);

            // Group data by featureId and build resolution map
            const featureMap = new Map<string, { 
              featureId: string;
              featureName: string;
              clusterResolutions: { [clusterReference: string]: 'access' | 'pass' | 'kill' };
            }>();
            
            rawData.forEach((item: FeatureMetricItem) => {
              const featureId = item.featureId || item.feature_id || '';
              const featureName = item.featureName || item.feature_name || item.name || '';
              const clusterRef = item.clusterReference || item.cluster_reference || '';
              const resolution = (item.resolution || 'pass').toLowerCase() as 'access' | 'pass' | 'kill';
              
              if (!featureMap.has(featureId)) {
                featureMap.set(featureId, {
                  featureId: featureId,
                  featureName: featureName,
                  clusterResolutions: {}
                });
              }
              featureMap.get(featureId)!.clusterResolutions[clusterRef] = resolution;
            });

            // Transform to Feature array
            const transformedFeatures: Feature[] = Array.from(featureMap.values()).map((featureData) => ({
              id: featureData.featureId,
              name: featureData.featureName,
              clusterResolutions: featureData.clusterResolutions,
            }));
            
            setFeatures(transformedFeatures);
            setOriginalFeatures(transformedFeatures);
            // The matrix now matches the server, so any prior edits are gone with it — e.g. a
            // reload triggered by an import, which replaced the matrix outright.
            setHasChanges(false);
          }
        } else {
          const errorMessage = typeof response.error === 'object'
            ? (response.error?.message || JSON.stringify(response.error))
            : (response.error || 'Failed to fetch feature access data');
          toast.error(errorMessage);
        }
      } catch (error) {
        rc.error(rc.getName(FeatureAccessPage), 'Error fetching feature access:', error);
        toast.error('Failed to fetch feature access data');
      } finally {
        setLoading(false);
      }
    };

    fetchFeatureAccess();
  }, [rc, selectedPilotId, reloadKey]);

  // Fetch available clusters for the Add Cluster dropdown (only Feature category clusters)
  useEffect(() => {
    const fetchClusters = async () => {
      if (!selectedPilotId) {
        return;
      }

      try {
        const response = await ClusterApis.getClustersList(rc, selectedPilotId.toString(), 'FEATURE');
        
        if (response.success && response.data) {
          const clusters = response.data.map((cluster: any) => ({
            clusterId: cluster.cluster_id || cluster.clusterId || '',
            clusterName: cluster.cluster_name || cluster.clusterName || '',
            clusterReference: cluster.cluster_reference || cluster.clusterReference || '',
          }));
          setAvailableClusters(clusters);
        }
      } catch (error) {
        rc.error(rc.getName(FeatureAccessPage), 'Error fetching clusters:', error);
      }
    };

    fetchClusters();
  }, [rc, selectedPilotId]);

  // Environments this project is associated with — the import's source choices.
  useEffect(() => {
    const fetchEnvironments = async () => {
      if (!selectedPilotId) return;
      try {
        const resp = await PilotApis.getAssociatedEnvironments(rc, String(selectedPilotId));
        setEnvironments(resp?.error ? [] : resp?.data?.data || []);
      } catch (error) {
        rc.error(rc.getName(FeatureAccessPage), 'Error fetching associated environments:', error);
        setEnvironments([]);
      }
    };

    fetchEnvironments();
  }, [rc, selectedPilotId]);

  /** Refetch the staged matrix for the current target — only meaningful for nonprod/prod, so
   *  direct-write targets just clear it. Re-run after a successful Stage Changes too. */
  const refreshStagedKeys = useCallback(async () => {
    if (!isStageOnlyEnv || !currentEnvId || !selectedPilotId) {
      setStagedKeys(new Set());
      return;
    }
    const resp = await DCXPromotionApis.getStagedSheets(rc, {
      target_env: currentEnvId,
      project_id: selectedPilotId.toString(),
      level: 'LEVEL_1',
    });
    if (resp.success && resp.data) {
      setStagedKeys(new Set(resp.data.data.map((row) => `${row.featureId}::${row.clusterReference}`)));
    }
  }, [rc, currentEnvId, isStageOnlyEnv, selectedPilotId]);

  useEffect(() => {
    refreshStagedKeys();
  }, [refreshStagedKeys, reloadKey]);

  /**
   * Replace this environment's whole feature access matrix with the chosen source's. The
   * server decides staged vs applied from the target's type — nonprod/prod never get written.
   */
  const handleImport = async () => {
    if (!selectedPilotId || !importSourceEnvId || !currentEnvId) return;

    setIsImporting(true);
    const resp = await DCXPromotionApis.importSheets(rc, {
      project_id: String(selectedPilotId),
      source_env: importSourceEnvId,
      target_env: currentEnvId,
      level: 'LEVEL_1',
    });
    setIsImporting(false);

    if (!resp.success || !resp.data) {
      toast.error(typeof resp.error === 'string' ? resp.error : resp.error?.message || 'Failed to import the access matrix');
      return;
    }

    const { mode, rows = 0, failed = 0 } = resp.data;
    setImportOpen(false);

    if (mode === 'staged') {
      toast.success(`Staged ${rows} resolutions for ${currentEnv ? envLabel(currentEnv) : 'this environment'} — nothing written yet.`);
    } else if (failed > 0) {
      toast.error(`Imported with ${failed} file(s) failing — check the products that didn't land.`);
    } else {
      toast.success(`Imported ${rows} resolutions into ${currentEnv ? envLabel(currentEnv) : 'this environment'}.`);
    }

    // Reload either way: an applied import rewrites the live matrix, and a staged one changes
    // what the feature-metric endpoint serves for nonprod/prod (staged overrides live) as well
    // as which cells come back staged.
    setReloadKey((k) => k + 1);
  };

  const handleBack = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS);
  };

  const handleAccessChange = (featureId: string, clusterReference: string, newStatus: 'access' | 'pass' | 'kill') => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.FEATURE_ACCESS_FEATURE_TOGGLED, {
      is_enabled: newStatus === 'access',
    });
    setFeatures(prev => prev.map(feature =>
      feature.id === featureId 
        ? { 
            ...feature, 
            clusterResolutions: { 
              ...feature.clusterResolutions, 
              [clusterReference]: newStatus 
            } 
          }
        : feature
    ));
    setHasChanges(true);
  };

  const handleDropdownOpen = (event: React.MouseEvent<HTMLElement>, featureId: string, column: string) => {
    const key = `${featureId}-${column}`;
    setDropdownAnchor(prev => ({ ...prev, [key]: event.currentTarget }));
  };

  const handleDropdownClose = (featureId: string, column: string) => {
    const key = `${featureId}-${column}`;
    setDropdownAnchor(prev => ({ ...prev, [key]: null }));
  };

  const handleSaveChanges = async () => {
    if (!selectedPilotId) {
      toast.error('No project selected');
      return;
    }

    setSaving(true);

    try {
      // Group features by component (EMAIL, DASHBOARD, HER) based on featureId prefix
      const componentGroups: { [key: string]: Feature[] } = {
        EMAIL: [],
        DASHBOARD: [],
        HER: []
      };

      features.forEach(feature => {
        const prefix = feature.id.split('.')[0].toUpperCase();
        if (prefix in componentGroups) {
          componentGroups[prefix].push(feature);
        }
      });

      // Build headers array from cluster columns
      // First column is always FEATURE_ID, followed by cluster references (keep original casing)
      const headers = ['FEATURE_ID', ...clusterColumns];

      // Build files array for each component that has features
      const files: {
        component: 'EMAIL' | 'DASHBOARD' | 'HER';
        level: 'L1' | 'L2';
        headers: string[];
        features: { featureId: string; values: string[] }[];
      }[] = [];

      (['EMAIL', 'DASHBOARD', 'HER'] as const).forEach(component => {
        const componentFeatures = componentGroups[component];
        if (componentFeatures.length > 0) {
          files.push({
            component,
            level: 'L1', // Using L1 as default based on current fetching level
            headers,
            features: componentFeatures.map(feature => ({
              featureId: feature.id,
              // Map cluster resolutions to values array in the same order as headers
              values: clusterColumns.map(clusterRef => {
                const resolution = feature.clusterResolutions[clusterRef] || 'pass';
                return resolution.toUpperCase();
              })
            }))
          });
        }
      });

      if (files.length === 0) {
        toast.error('No features to save');
        setSaving(false);
        return;
      }

      const payload = {
        projectId: selectedPilotId.toString(),
        files
      };

      const response = await ClusterApis.saveFeatureAccess(rc, payload);

      if (response.success && response.data) {
        const responseData = response.data;
        
        // Check if all files were uploaded and API was notified
        const uploadedFiles = responseData.uploaded_files || [];
        const allNotified = uploadedFiles.every(file => file.api_notified);
        
        if (allNotified) {
          toast.success(responseData.message || 'Feature access saved successfully');
        } else {
          // Some files may have notification errors
          const failedFiles = uploadedFiles.filter(file => !file.api_notified);
          const failedNames = failedFiles.map(f => f.file_name).join(', ');
          toast.warning(`Feature access saved, but some files failed to notify backend: ${failedNames}`);
        }

        rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.FEATURE_ACCESS_SAVED, {
          feature_count: features.length,
          cluster_count: clusterColumns.length,
        });

        // Show any additional errors
        if (responseData.errors && responseData.errors.length > 0) {
          responseData.errors.forEach((error: any) => {
            const errorMessage = typeof error === 'object' ? (error.message || JSON.stringify(error)) : error;
            toast.error(errorMessage);
          });
        }

        setHasChanges(false);
      } else {
        // Handle error object with code/message structure
        const errorMessage = typeof response.error === 'object' 
          ? (response.error?.message || JSON.stringify(response.error)) 
          : (response.error || 'Failed to save feature access');
        toast.error(errorMessage);
      }
    } catch (error) {
      rc.error(rc.getName(FeatureAccessPage), 'Error saving feature access:', error);
      toast.error('Failed to save feature access');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Stage Changes — nonprod/prod targets. The stage endpoint (PUT .../sheets/stage) takes every
   * changed cell as one batch and is update-only: cells this environment has never staged come
   * back under `notStaged` (Import Sheet seeds them) instead of failing the batch. So this
   * diffs against the last-loaded snapshot and sends the result in a single call.
   */
  const handleStageChanges = async () => {
    if (!currentEnvId || !selectedPilotId) {
      toast.error('No environment selected');
      return;
    }

    setSaving(true);

    try {
      const changedCells: { featureId: string; clusterReference: string; resolution: string }[] = [];
      features.forEach((feature) => {
        const original = originalFeatures.find((f) => f.id === feature.id);
        clusterColumns.forEach((clusterRef) => {
          const current = feature.clusterResolutions[clusterRef] ?? 'pass';
          const before = original?.clusterResolutions[clusterRef] ?? 'pass';
          if (current !== before) {
            changedCells.push({ featureId: feature.id, clusterReference: clusterRef, resolution: current.toUpperCase() });
          }
        });
      });

      if (changedCells.length === 0) {
        toast.warning('No changes to stage');
        return;
      }

      const resp = await DCXPromotionApis.stageSheetRows(rc, {
        target_env: currentEnvId,
        project_id: selectedPilotId.toString(),
        rows: changedCells.map((cell) => ({
          featureId: cell.featureId,
          clusterReference: cell.clusterReference,
          resolution: cell.resolution,
        })),
      });

      if (!resp.success || !resp.data) {
        const message = typeof resp.error === 'string' ? resp.error : (resp.error as any)?.message;
        toast.error(message || 'Failed to stage feature access changes');
        return;
      }

      const succeededKeys = new Set(
        (resp.data.rows?.updated ?? []).map((row) => `${row.featureId}::${row.clusterReference}`)
      );
      const notStagedYet = resp.data.rows?.notStaged?.length ?? 0;

      if (succeededKeys.size > 0) {
        setOriginalFeatures((prev) =>
          prev.map((f) => {
            const current = features.find((cf) => cf.id === f.id);
            if (!current) return f;
            const updatedResolutions = { ...f.clusterResolutions };
            clusterColumns.forEach((clusterRef) => {
              if (succeededKeys.has(`${f.id}::${clusterRef}`)) {
                updatedResolutions[clusterRef] = current.clusterResolutions[clusterRef] ?? 'pass';
              }
            });
            return { ...f, clusterResolutions: updatedResolutions };
          })
        );

        rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_TOOLS.FEATURE_ACCESS_SAVED, {
          feature_count: features.length,
          cluster_count: clusterColumns.length,
        });

        refreshStagedKeys();
      }

      if (notStagedYet > 0) {
        toast.warning(
          `${notStagedYet} change(s) couldn't be staged — ${currentEnv ? envLabel(currentEnv) : 'this environment'} has no imported matrix yet. Use "Import Sheet" first.`
        );
      }
      if (succeededKeys.size === changedCells.length) {
        toast.success(`Staged ${changedCells.length} change(s) for ${currentEnv ? envLabel(currentEnv) : 'this environment'}.`);
        setHasChanges(false);
      } else if (succeededKeys.size > 0) {
        toast.success(`Staged ${succeededKeys.size} of ${changedCells.length} change(s).`);
      }
    } catch (error) {
      rc.error(rc.getName(FeatureAccessPage), 'Error staging feature access changes:', error);
      toast.error('Failed to stage feature access changes');
    } finally {
      setSaving(false);
    }
  };

  const handleAddClusterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAddClusterAnchor(event.currentTarget);
  };

  const handleAddClusterClose = () => {
    setAddClusterAnchor(null);
  };

  const handleClusterTypeSelect = (clusterReference: string) => {
    // Check if cluster is already added
    if (clusterColumns.includes(clusterReference)) {
      handleAddClusterClose();
      return;
    }

    // Add the cluster as a new column
    setClusterColumns(prev => [...prev, clusterReference]);

    // Set default 'pass' resolution for all features in this new cluster
    setFeatures(prev => prev.map(feature => ({
      ...feature,
      clusterResolutions: {
        ...feature.clusterResolutions,
        [clusterReference]: 'pass'
      }
    })));

    setHasChanges(true);
    handleAddClusterClose();
  };

  const handleRemoveCluster = (clusterReference: string) => {
    // Remove the cluster column
    setClusterColumns(prev => prev.filter(col => col !== clusterReference));

    // Remove the cluster resolution from all features
    setFeatures(prev => prev.map(feature => {
      const { [clusterReference]: removed, ...remainingResolutions } = feature.clusterResolutions;
      return {
        ...feature,
        clusterResolutions: remainingResolutions
      };
    }));

    setHasChanges(true);
  };

  // Filter out clusters that are already added as columns
  const availableClusterOptions = availableClusters.filter(
    cluster => !clusterColumns.includes(cluster.clusterReference)
  );

  const filteredFeatures = filter === 'all' 
    ? features 
    : features.filter(feature => feature.id.toLowerCase().includes(filter.toLowerCase()));

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: 'background.default',
      p: 3,
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Feature Access
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={16} />}
            onClick={handleBack}
            sx={{ color: 'text.secondary', borderColor: 'divider' }}
          >
            Back to Cluster Configuration
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ mx: 'auto' }}>
        <StyledCard>
          <CardContent sx={{ p: 4,textAlign:'left' }}>
            {/* Feature Access Matrix Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: .5 }}>
                    <Key size={24} style={{ marginRight: 12, color: theme.palette.primary.main }} />
                    <Typography variant="h5" sx={{ fontWeight: 600}}>
                        Feature Access Matrix
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Manage feature accessibility based on 'Feature' category clusters. This list is derived from the Application Scope page; only enabled applications are displayed here.
                </Typography>
              </Box>
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  startIcon={<Download size={16} />}
                  onClick={() => {
                    setImportSourceEnvId(importSourceOptions[0] ? String(importSourceOptions[0].id) : '');
                    setImportOpen(true);
                  }}
                  disabled={importSourceOptions.length === 0 && unmappedSourceAppEnvs.length === 0}
                  sx={{ color: 'text.secondary', borderColor: 'divider' }}
                >
                  Import Sheet
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Plus size={16} />}
                  endIcon={<ChevronDown size={16} />}
                  onClick={handleAddClusterClick}
                  aria-controls={addClusterOpen ? 'add-cluster-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={addClusterOpen ? 'true' : undefined}
                  sx={{ color: 'text.secondary', borderColor: 'divider' }}
                >
                  Add Cluster
                </Button>
                <Menu
                  id="add-cluster-menu"
                  anchorEl={addClusterAnchor}
                  open={addClusterOpen}
                  onClose={handleAddClusterClose}
                  MenuListProps={{
                    'aria-labelledby': 'add-cluster-button',
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    sx: {
                      borderRadius: 2,
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                      border: '1px solid #E0E0E0',
                      minWidth: '160px',
                      '& .MuiMenuItem-root': {
                        padding: '12px 16px',
                        fontSize: '14px',
                        fontWeight: 500,
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                      },
                    },
                  }}
                >
                  {availableClusterOptions.length > 0 ? (
                    availableClusterOptions.map((cluster) => (
                      <MenuItem 
                        key={cluster.clusterId || cluster.clusterReference} 
                        onClick={() => handleClusterTypeSelect(cluster.clusterReference)}
                      >
                        {cluster.clusterReference || cluster.clusterName}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      {availableClusters.length === 0 ? 'Loading clusters...' : 'All clusters added'}
                    </MenuItem>
                  )}
                </Menu>
              </Box>
            </Box>

            {/* Filter */}
            <Box sx={{ mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 300 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary',marginBottom:.5 }}>Filter by Feature</Typography>
                <Select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                //   label=""
                >
                  <MenuItem value="all">All Features</MenuItem>
                  {features.map((feature) => (
                    <MenuItem key={feature.id} value={feature.id}>{featureLabel(feature)}</MenuItem>
                  ))}
                  {/* <MenuItem value="email">Email Features</MenuItem>
                  <MenuItem value="dashboard">Dashboard Features</MenuItem> */}
                </Select>
              </FormControl>
            </Box>

            {/* Feature Access Table */}
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                py: 8,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                bgcolor: 'background.default'
              }}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                  Loading feature access data...
                </Typography>
              </Box>
            ) : features.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 8, 
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                bgcolor: 'background.default'
              }}>
                <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                  No features found
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No feature access data available for this pilot.
                </Typography>
              </Box>
            ) : (
            <TableContainer component={Paper} sx={{ 
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden',
            }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'background.default' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Feature
                        <Tooltip title="Feature access configuration">
                          <Info size={16} style={{ marginLeft: 4, color: theme.palette.text.secondary }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    {/* Dynamic cluster columns */}
                    {clusterColumns.map((clusterRef, index) => {
                      const isDefaultCluster = index === 0;
                      return (
                        <TableCell 
                          key={clusterRef} 
                          align="center" 
                          sx={{ 
                            fontWeight: 600,
                            '&:hover .remove-column-btn': {
                              opacity: 1,
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            {clusterRef.toUpperCase()}
                            {!isDefaultCluster && (
                              <Tooltip title="Remove column">
                                <IconButton
                                  className="remove-column-btn"
                                  size="small"
                                  onClick={() => handleRemoveCluster(clusterRef)}
                                  sx={{
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    p: 0.5,
                                    bgcolor: 'primary.main',
                                    color: 'error.main',
                                    '&:hover': {
                                      bgcolor: 'primary.dark',
                                    },
                                  }}
                                >
                                  <X size={14} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFeatures.map((feature) => {
                    const rowStaged = clusterColumns.some((clusterRef) => stagedKeys.has(`${feature.id}::${clusterRef}`));
                    return (
                    <TableRow
                      key={feature.id}
                      hover
                      sx={rowStaged ? {
                        bgcolor: 'rgba(147,51,234,0.04)',
                        outline: '1px solid #9333ea',
                        outlineOffset: '-1px',
                      } : undefined}
                    >
                      <TableCell sx={{px:1,py:0.5}}>
                        <Tooltip title={feature.id} placement="top" arrow>
                          <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
                            {featureLabel(feature)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      {/* Dynamic cluster cells */}
                      {clusterColumns.map((clusterRef) => {
                        const resolution = feature.clusterResolutions[clusterRef] || 'pass';
                        return (
                          <TableCell key={clusterRef} align="center" sx={{px:1,py:0.5, textAlign: 'center'}}>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <AccessButton
                                status={resolution}
                                staged={stagedKeys.has(`${feature.id}::${clusterRef}`)}
                                onClick={(e) => handleDropdownOpen(e, feature.id, clusterRef)}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
                                  {getStatusIcon(resolution)}
                                  {resolution.toUpperCase()}
                                </Box>
                                <ChevronDown size={14} />
                              </AccessButton>
                            </Box>
                            <AccessStatusDropdown
                              status={resolution}
                              anchorEl={dropdownAnchor[`${feature.id}-${clusterRef}`]}
                              onClose={() => handleDropdownClose(feature.id, clusterRef)}
                              onSelect={(newStatus) => handleAccessChange(feature.id, clusterRef, newStatus)}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );})}
                </TableBody>
              </Table>
            </TableContainer>
            )}

            {/* Save Changes / Stage Changes Button — nonprod/prod targets can't be written to
                directly, so they stage instead (see handleStageChanges). */}
            {hasChanges && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <PermissionGate require={UI_PAGES.FEATURE_ACCESS.ctas.feature_resolution.id} mode="disable">
                  {isStageOnlyEnv ? (
                    <BglPrimaryButton
                      startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Layers size={16} />}
                      onClick={handleStageChanges}
                      disabled={saving}
                    >
                      {saving ? 'Staging...' : 'Stage Changes'}
                    </BglPrimaryButton>
                  ) : (
                    <BglPrimaryButton
                      startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      onClick={handleSaveChanges}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </BglPrimaryButton>
                  )}
                </PermissionGate>
              </Box>
            )}
          </CardContent>
        </StyledCard>
      </Box>

      {/* Import Access Matrix — replaces this environment's whole matrix from another env. */}
      <Dialog open={importOpen} onClose={() => !isImporting && setImportOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Download size={20} color={theme.palette.primary.main} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Import Access Matrix</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Select a source environment to import the full feature access configuration.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography
            sx={{ fontSize: 11, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}
          >
            Source Environment
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={importSourceEnvId}
              onChange={(e) => setImportSourceEnvId(e.target.value)}
              disabled={isImporting}
              sx={{ borderRadius: 1.5 }}
            >
              {importSourceOptions.map((env) => (
                <MenuItem key={String(env.id)} value={String(env.id)}>
                  {envLabel(env)}
                </MenuItem>
              ))}
              {unmappedSourceAppEnvs.map((appEnv) => (
                <MenuItem key={`unmapped-${appEnv}`} value={`unmapped-${appEnv}`} disabled>
                  {formatAppEnvLabel(appEnv)} — not mapped to this pilot
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              mt: 3,
              p: 2,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: alpha(theme.palette.error.main, 0.2),
              bgcolor: alpha(theme.palette.error.main, 0.05),
            }}
          >
            <AlertTriangle size={16} color={theme.palette.error.main} style={{ flexShrink: 0, marginTop: 2 }} />
            <Box>
              <Typography sx={{ fontWeight: 700, color: 'error.main', mb: 0.5 }}>Override Warning</Typography>
              <Typography sx={{ fontSize: 12, lineHeight: 1.6, color: 'error.main' }}>
                Importing will replace the entire feature access matrix in the current environment (
                <Box component="strong" sx={{ color: 'text.primary' }}>
                  {currentEnv ? envLabel(currentEnv) : 'this environment'}
                </Box>
                ). This cannot be undone.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" disabled={isImporting} onClick={() => setImportOpen(false)} sx={{ color: 'text.secondary', borderColor: 'divider' }}>
            Cancel
          </Button>
          <BglPrimaryButton onClick={handleImport} disabled={isImporting || !importSourceEnvId}>
            {isImporting ? (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} sx={{ color: 'inherit' }} /> Importing...
              </Box>
            ) : (
              'Confirm & Import'
            )}
          </BglPrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
